import React, { useState } from 'react';
import { Paper, Typography, Box, Chip, IconButton, CircularProgress } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { ISource, IChunk } from '../../../Types/Docs';
import { toast } from '../../../Utils/toast';

interface SourcesListProps {
  sources: ISource[];
  chunks?: IChunk[];
  'data-testid'?: string;
}

const SourcesList: React.FC<SourcesListProps> = ({ sources, chunks = [], 'data-testid': testId }) => {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Create a map of documentId to chunks for easy lookup of categories
  const chunksByDocumentId = React.useMemo(() => {
    const map = new Map<string, IChunk>();
    chunks.forEach(chunk => {
      if (!map.has(chunk.documentId)) {
        map.set(chunk.documentId, chunk);
      }
    });
    return map;
  }, [chunks]);

  const handleDownload = async (documentId: string, documentName: string) => {
    setDownloadingId(documentId);
    try {
      const { downloadDocument } = await import('../../../Utils/docsApi');
      const blob = await downloadDocument(documentId);
      
      // Validate blob
      if (!blob || blob.size === 0) {
        throw new Error('Received empty file');
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = documentName || 'document';
      window.document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        window.document.body.removeChild(a);
      }, 100);
      
      toast.success('Download started');
    } catch (error: any) {
      console.error('Download error:', error);
      const errorMessage = error?.message || error?.response?.data?.message || 'Failed to download document';
      toast.error(errorMessage);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Paper sx={{ p: 3 }} data-testid={testId}>
      <Typography variant="h3" sx={{ mb: 2 }}>
        Sources ({sources.length})
      </Typography>
      <Box display="flex" flexDirection="column" gap={2}>
        {sources.map((source, index) => {
          const chunk = chunksByDocumentId.get(source.documentId);
          const categories = chunk?.categories || [];
          
          return (
            <Box
              key={source.documentId}
              sx={{
                p: 2,
                border: '1px solid #DEDEED',
                borderRadius: 1,
                '&:hover': { bgcolor: '#F8F8F8' },
              }}
              data-testid={`${testId}-source-${index}`}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {source.documentName}
                    </Typography>
                    <Chip
                      label={`${Math.round(source.relevanceScore * 100)}%`}
                      size="small"
                      color="primary"
                      sx={{ ml: 2 }}
                    />
                  </Box>
                  {categories.length > 0 && (
                    <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                      {categories.map((category: string, idx: number) => (
                        <Chip
                          key={idx}
                          label={category}
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
                <IconButton
                  onClick={() => handleDownload(source.documentId, source.documentName)}
                  disabled={downloadingId === source.documentId}
                  aria-label={`Download ${source.documentName}`}
                  data-testid={`${testId}-download-${source.documentId}`}
                  sx={{ ml: 2 }}
                >
                  {downloadingId === source.documentId ? (
                    <CircularProgress size={20} />
                  ) : (
                    <DownloadIcon />
                  )}
                </IconButton>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default SourcesList;
